import { useNavigate, useParams } from 'react-router';

import {
    PacksDrawerDetails,
    packsDrawerFormFromRecord,
} from '../PacksDrawerDetails/PacksDrawerDetails';
import { Loading, Modal, Section } from '../../shared';

import {
    useDeletePacksDrawer,
    usePacksDrawerRecord,
    useUpdatePacksDrawer,
} from '../../hooks/packsDrawer';

import { handleError } from '../../utils/utils';

import { RouteTo } from '../../routes';

import { MediaTypeUnion } from '../../types/enums/mediatype.enum';

import { Media } from '../../types/mediaTypes';

function toMedia(record: {
    thumbnail: { id: number; type: string; url: string } | null;
}): Media | null {
    if (!record.thumbnail) {
        return null;
    }

    return {
        id: record.thumbnail.id,
        type: record.thumbnail.type as MediaTypeUnion, // server returns the media type as a plain string
        url: record.thumbnail.url,
        md5: '',
        created: '',
        lastModified: '',
        deletedAt: null,
        processingProgress: null,
        processingStatus: null,
        originalFileName: null,
    };
}

export const PacksDrawerUpdatePage = () => {
    const navigate = useNavigate();
    const params = useParams();
    const recordId = params.id as string;

    const {
        record: fetchedRecord,
        isLoading: isFetchLoading,
        errorMessage: fetchError,
    } = usePacksDrawerRecord(recordId);
    const {
        updatedRecord,
        isLoading: isUpdateLoading,
        updateRecord,
    } = useUpdatePacksDrawer(recordId);
    const { deleteRecord } = useDeletePacksDrawer();

    const record = updatedRecord ?? fetchedRecord;

    async function onSave(form: {
        packId: string;
        active: boolean;
        isFree: boolean;
        thumbnailMediaId: number | null;
    }) {
        try {
            await updateRecord({
                active: form.active,
                isFree: form.isFree,
                thumbnailMediaId: form.thumbnailMediaId,
            });
            return true;
        } catch {
            return false;
        }
    }

    const onDelete = async () => {
        const isConfirmed = await Modal.confirm({
            heading: 'Delete packs drawer record',
            text: 'Really delete this packs drawer record?',
            okLabel: 'Yes do it!',
        });

        if (!isConfirmed) return;

        try {
            await deleteRecord(Number(recordId));
            navigate(RouteTo.PacksDrawer());
        } catch (error) {
            handleError('Error deleting packs drawer record', error);
        }
    };

    if (isFetchLoading) {
        return <Loading />;
    }

    if (fetchError || !record) {
        return (
            <Section>
                {fetchError ?? 'The packs drawer record does not exist'}
            </Section>
        );
    }

    return (
        <PacksDrawerDetails
            isNew={false}
            onSave={onSave}
            isSaving={isUpdateLoading}
            initialForm={packsDrawerFormFromRecord(record)}
            initialThumbnail={toMedia(record) ?? undefined}
            onDelete={onDelete}
        />
    );
};
